import { flow, types } from "mobx-state-tree";
import { withRootStore } from "mobx-stores/_extension/with-root-store";
import {
  IMaterialGoodsIssueProperties,
  MaterialGoodsIssue,
} from "mobx-stores/_models/material-inventory/material-goods-issue/material-goods-issue";
import {
  IMaterialGoodsReceiptProperties,
  MaterialGoodsReceipt,
} from "mobx-stores/_models/material-inventory/material-goods-receipt/material-goods-receipt";
import {
  IMaterialInventoryProperties,
  MaterialInventory,
} from "mobx-stores/_models/material-inventory/material-inventory";

import { materialGoodsIssueBusiness } from "api/bussiness/material/material-goods-issue";
import { materialGoodsReceiptBusiness } from "api/bussiness/material/material-goods-receipt";
import { materialInventoryBussiness } from "api/bussiness/material/material-inventory";
import {
  GetMaterialGoodsIssue,
  GetMaterialGoodsReceipt,
  GetMaterialsInventory,
} from "api/models/request/material/get-paper-material-inventory";
import { Pagination } from "api/models/response/pagination";
import { MaterialInventoryTab } from "pages/material-inventory/material-inventory";
import { withSetPropAction } from "../_extension/with-set-props";
import { DateRangeFilterStore } from "./common/date-range-filter-store";
import { PaginationStore } from "./common/pagination-store";
export const MaterialInventoryQueryParams = types
  .model({
    type: "",
    name: "",
    code: "",
    barcode: "",
    showZeroQuantity: false,
    isExactMatch: true,
  })
  .actions(withSetPropAction);
const MaterialInventoryStore = types
  .model({
    materialsInventory: types.optional(types.array(MaterialInventory), []),
    materialDetail: types.maybeNull(MaterialInventory),
    pagination: types.optional(PaginationStore, {}),
    openNew: false,
    openEdit: false,
    openDelete: false,
    queryParams: types.optional(MaterialInventoryQueryParams, {}),
  })
  .extend(withRootStore)
  .actions(withSetPropAction)
  .actions((self) => ({
    getMaterialsInventory: flow(function* getMaterialsInventory(
      request?: GetMaterialsInventory,
    ) {
      try {
        self._rootStore.setProp("inProgress", true);
        if (!request) {
          request = {
            filter: self.pagination.filter,
            maxResultCount: self.pagination.itemsPerPage,
            skipCount: self.pagination.skipCount,
            sorting: "",
            name: self.queryParams.name,
            type: self.queryParams.type,
            code: self.queryParams.code,
            barcode: self.queryParams.barcode,
            showZeroQuantity: self.queryParams.showZeroQuantity,
            isExactMatch: self.queryParams.isExactMatch,
          };
        }
        const res = (yield materialInventoryBussiness.getMaterialsInventory(
          request,
        )) as Pagination<IMaterialInventoryProperties>;
        self.setProp("materialsInventory", res.items);
        self.pagination.setProp("totalItems", res.totalCount);
      } catch (ex) {
        console.error(ex);
      } finally {
        self._rootStore.setProp("inProgress", false);
      }
    }),
  }));

export const MaterialGoodsReceiptQueryParams = types
  .model({
    type: "",
    name: "",
    code: "",
    barcode: "",
    dateRangeFilter: types.optional(DateRangeFilterStore, {}),
    showZeroQuantity: false,
    isExactMatch: true,
  })
  .actions(withSetPropAction);
const MaterialGoodsReceiptStore = types
  .model({
    materialGoodsReceipts: types.optional(
      types.array(MaterialGoodsReceipt),
      [],
    ),
    materialGoodsReceiptDetail: types.maybeNull(MaterialGoodsReceipt),
    pagination: types.optional(PaginationStore, {}),
    openNew: false,
    openEdit: false,
    openDelete: false,
    materialFilterValue: "",
    queryParams: types.optional(MaterialGoodsReceiptQueryParams, {}),
  })
  .extend(withRootStore)
  .actions(withSetPropAction)
  .actions((self) => ({
    getMaterialGoodsReceipt: flow(function* getMaterialGoodsReceipt(
      request?: GetMaterialGoodsReceipt,
    ) {
      try {
        self._rootStore.setProp("inProgress", true);
        if (!request) {
          request = {
            filter: self.pagination.filter,
            maxResultCount: self.pagination.itemsPerPage,
            skipCount: self.pagination.skipCount,
            sorting: "",
            fromReceiptDate:
              self.queryParams.dateRangeFilter.fromAsDayJs?.isValid() &&
              self.queryParams.dateRangeFilter.from
                ? self.queryParams.dateRangeFilter.from
                : undefined,
            toReceiptDate:
              self.queryParams.dateRangeFilter.toAsDayJs?.isValid() &&
              self.queryParams.dateRangeFilter.to
                ? self.queryParams.dateRangeFilter.to
                : undefined,
            name: self.queryParams.name,
            type: self.queryParams.type,
            code: self.queryParams.code,
            barcode: self.queryParams.barcode,
            showZeroQuantity: self.queryParams.showZeroQuantity,
            isExactMatch: self.queryParams.isExactMatch,
          };
        }
        const res = (yield materialGoodsReceiptBusiness.getLists(
          request,
        )) as Pagination<IMaterialGoodsReceiptProperties>;
        self.setProp("materialGoodsReceipts", res.items);
        self.pagination.setProp("totalItems", res.totalCount);
      } catch (ex) {
        console.error(ex);
      } finally {
        self._rootStore.setProp("inProgress", false);
      }
    }),
  }));

export const MaterialGoodsIssueQueryParams = types
  .model({
    type: "",
    name: "",
    code: "",
    barcode: "",
    dateRangeFilter: types.optional(DateRangeFilterStore, {}),
    showZeroQuantity: false,
    isExactMatch: true,
  })
  .actions(withSetPropAction);
const MaterialGoodsIssueStore = types
  .model({
    materialGoodsIssues: types.optional(types.array(MaterialGoodsIssue), []),
    materialGoodsIssueDetail: types.maybeNull(MaterialGoodsIssue),
    pagination: types.optional(PaginationStore, {}),
    openNew: false,
    openEdit: false,
    openDelete: false,
    materialFilterValue: "",
    queryParams: types.optional(MaterialGoodsIssueQueryParams, {}),
  })
  .extend(withRootStore)
  .actions(withSetPropAction)
  .actions((self) => ({
    getMaterialGoodsIssue: flow(function* getMaterialGoodsIssue(
      request?: GetMaterialGoodsIssue,
    ) {
      try {
        self._rootStore.setProp("inProgress", true);
        if (!request) {
          request = {
            filter: self.pagination.filter,
            maxResultCount: self.pagination.itemsPerPage,
            skipCount: self.pagination.skipCount,
            sorting: "",
            fromIssueDate:
              self.queryParams.dateRangeFilter.fromAsDayJs?.isValid() &&
              self.queryParams.dateRangeFilter.from
                ? self.queryParams.dateRangeFilter.from
                : undefined,
            toIssueDate:
              self.queryParams.dateRangeFilter.toAsDayJs?.isValid() &&
              self.queryParams.dateRangeFilter.to
                ? self.queryParams.dateRangeFilter.to
                : undefined,
            type: self.queryParams.type,
            name: self.queryParams.name,
            code: self.queryParams.code,
            barcode: self.queryParams.barcode,
            showZeroQuantity: self.queryParams.showZeroQuantity,
            isExactMatch: self.queryParams.isExactMatch,
          };
        }
        const res = (yield materialGoodsIssueBusiness.getLists(
          request,
        )) as Pagination<IMaterialGoodsIssueProperties>;
        self.setProp("materialGoodsIssues", res.items);
        self.pagination.setProp("totalItems", res.totalCount);
      } catch (ex) {
        console.error(ex);
      } finally {
        self._rootStore.setProp("inProgress", false);
      }
    }),
  }));

export const MaterialInventoryManagement = types
  .model({
    materialInventoryStore: types.optional(MaterialInventoryStore, {}),
    materialGoodsReceiptStore: types.optional(MaterialGoodsReceiptStore, {}),
    materialGoodsIssueStore: types.optional(MaterialGoodsIssueStore, {}),
    tabSelected: types.maybeNull(
      types.enumeration<MaterialInventoryTab>(
        "MaterialInventoryTab",
        Object.values(MaterialInventoryTab),
      ),
    ),
  })
  .extend(withRootStore)
  .actions(withSetPropAction);
